import { useEffect, useState } from "react";
import { motion } from "motion/react";
import {
  Shield,
  CheckCircle,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Database,
} from "lucide-react";
import type { ExceptionItem, ExceptionsData } from "../../types/dashboard";
import { API_URL } from "../../../constants/index";
import { getAPI } from "../../../api/frameworksApi";

interface DashboardEnvelope<T> {
  status: string;
  apiVersion: string;
  requestId: string;
  timestamp: string;
  data: T;
  pagination: unknown;
  errors: unknown;
}

interface ExceptionApiItem {
  riskAdjustmentId: string;
  poamItemId: string;
  poamItemTitle: string;
  controlId: string;
  adjustmentType: "risk_acceptance" | "false_positive" | "risk_adjustment";
  lifecycleState: "Approved" | "In Review" | "Draft";
  justification: string;
  adjustedRiskLevel: string | null;
  effectiveFrom: string | null;
  effectiveUntil: string | null;
  daysRemaining: number | null;
  expiringSoon: boolean;
  priorityOrder: number;
}

interface ExceptionsApiData {
  exceptions: ExceptionApiItem[];
  totalExceptions: number;
  approved: number;
  pendingReview: number;
  expiringSoon: number;
}

function normalizeLifecycleState(
  lifecycleState: ExceptionApiItem["lifecycleState"],
): ExceptionItem["lifecycleState"] {
  if (lifecycleState === "Approved") return "approved";
  if (lifecycleState === "In Review") return "in_review";
  return "draft";
}

function normalizeExceptionsData(data: ExceptionsApiData): ExceptionsData {
  return {
    totalExceptions: data.totalExceptions ?? 0,
    approved: data.approved ?? 0,
    pendingReview: data.pendingReview ?? 0,
    expiringSoon: data.expiringSoon ?? 0,
    items: (data.exceptions ?? []).map((item) => ({
      id: item.riskAdjustmentId,
      priorityOrder: item.priorityOrder,
      controlId: item.controlId,
      poamItemTitle: item.poamItemTitle,
      adjustmentType: item.adjustmentType,
      lifecycleState: normalizeLifecycleState(item.lifecycleState),
      adjustedRiskLevel: item.adjustedRiskLevel ?? "n/a",
      justification: item.justification,
      effectiveUntil: item.effectiveUntil,
      daysRemaining: item.daysRemaining,
      expiringSoon: item.expiringSoon,
    })),
  };
}

function formatDate(iso: string | null) {
  if (!iso) return "No Expiry";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const typeBadge: Record<ExceptionItem["adjustmentType"], { label: string; cls: string }> = {
  risk_acceptance: {
    label: "Risk Acceptance",
    cls: "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400",
  },
  false_positive: {
    label: "False Positive",
    cls: "bg-sky-100 dark:bg-sky-500/20 text-sky-700 dark:text-sky-400",
  },
  risk_adjustment: {
    label: "Risk Adjustment",
    cls: "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400",
  },
};

const lifecycleBadge: Record<ExceptionItem["lifecycleState"], { label: string; cls: string }> = {
  approved: {
    label: "Approved",
    cls: "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400",
  },
  in_review: {
    label: "In Review",
    cls: "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400",
  },
  draft: {
    label: "Draft",
    cls: "bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-white/40",
  },
};

const riskColor: Record<string, string> = {
  critical: "text-red-600 dark:text-red-400",
  high: "text-orange-600 dark:text-orange-400",
  medium: "text-amber-600 dark:text-amber-400",
  low: "text-slate-500 dark:text-white/40",
  "n/a": "text-[#94a3b8] dark:text-white/30",
};

const kpiAccent: Record<string, { icon: string; ring: string }> = {
  indigo: {
    icon: "text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-500/15",
    ring: "border-l-indigo-500",
  },
  emerald: {
    icon: "text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-500/15",
    ring: "border-l-emerald-500",
  },
  amber: {
    icon: "text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/15",
    ring: "border-l-amber-500",
  },
  red: {
    icon: "text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-500/15",
    ring: "border-l-red-500",
  },
};

function PanelState({
  title,
  body,
  tone = "neutral",
}: {
  title: string;
  body: string;
  tone?: "neutral" | "error";
}) {
  const isError = tone === "error";
  return (
    <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] px-6 py-10 text-center">
      <div
        className={`mx-auto mb-3 flex size-10 items-center justify-center rounded-full ${
          isError
            ? "bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400"
            : "bg-slate-100 text-slate-500 dark:bg-white/[0.06] dark:text-white/40"
        }`}
      >
        <Database className="size-4" />
      </div>
      <p className="text-sm font-bold text-[#0f172a] dark:text-white">{title}</p>
      <p className="mt-1 text-xs text-[#64748b] dark:text-white/40">{body}</p>
    </div>
  );
}

export function ExceptionsTab() {
  const [expandedJustification, setExpandedJustification] = useState<string | null>(null);
  const [data, setData] = useState<ExceptionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadExceptions = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getAPI(API_URL.DASHBOARD_EXCEPTIONS) as DashboardEnvelope<ExceptionsApiData>;
        if (cancelled) return;
        setData(normalizeExceptionsData(response.data));
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load dashboard exceptions", err);
        setError("Unable to load exception register right now.");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadExceptions();

    return () => {
      cancelled = true;
    };
  }, []);

  const kpis = [
    { label: "Total Exceptions", value: data?.totalExceptions ?? 0, icon: Shield, accent: "indigo" },
    { label: "Approved", value: data?.approved ?? 0, icon: CheckCircle, accent: "emerald" },
    { label: "Pending Review", value: data?.pendingReview ?? 0, icon: Clock, accent: "amber" },
    {
      label: "Expiring Soon",
      value: data?.expiringSoon ?? 0,
      icon: AlertTriangle,
      accent: "red",
      highlight: (data?.expiringSoon ?? 0) > 0,
    },
  ];

  if (loading && !data) {
    return (
      <PanelState
        title="Loading exception register"
        body="Fetching the Platform Owner exceptions dashboard."
      />
    );
  }

  if (error && !data) {
    return <PanelState title="Exceptions unavailable" body={error} tone="error" />;
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {kpis.map((kpi, i) => {
          const Icon = kpi.icon;
          const ac = kpiAccent[kpi.accent];
          return (
            <motion.div
              key={kpi.label}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07, duration: 0.35 }}
              className={`bg-white dark:bg-[#131320] rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] border-l-[3px] ${ac.ring} p-4 hover:shadow-md dark:hover:shadow-black/30 transition-all hover:-translate-y-px ${
                "highlight" in kpi && kpi.highlight
                  ? "ring-2 ring-red-500/20 dark:ring-red-500/30"
                  : ""
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`size-9 rounded-lg flex items-center justify-center ${ac.icon}`}>
                  <Icon className="size-4" />
                </div>
                <div>
                  <p className="text-2xl font-black text-[#0f172a] dark:text-white tracking-tight leading-none">
                    {kpi.value}
                  </p>
                  <p className="text-xs font-bold text-[#0f172a] dark:text-white/70 mt-0.5">
                    {kpi.label}
                  </p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        className="bg-white dark:bg-[#131320] rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-[#f1f5f9] dark:border-white/[0.04]">
          <h3 className="text-sm font-bold text-[#0f172a] dark:text-white">
            Exception Register
          </h3>
          <p className="text-[10px] text-[#94a3b8] dark:text-white/30 mt-0.5">
            Risk exceptions and false-positive designations
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[#f1f5f9] dark:border-white/[0.04]">
                {[
                  "#",
                  "Control",
                  "Finding",
                  "Type",
                  "Lifecycle",
                  "Risk Level",
                  "Justification",
                  "Valid Until",
                  "Days Left",
                ].map((h) => (
                  <th
                    key={h}
                    className="text-center px-3 py-2.5 text-[10px] font-bold tracking-wide text-[#94a3b8] dark:text-white/30"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-5 py-10 text-center text-xs text-[#94a3b8] dark:text-white/30">
                    No exceptions found.
                  </td>
                </tr>
              ) : (
                (data?.items ?? []).map((item) => {
                  const tb = typeBadge[item.adjustmentType];
                  const lb = lifecycleBadge[item.lifecycleState];
                  const rc = riskColor[item.adjustedRiskLevel.toLowerCase()] || "text-[#64748b] dark:text-white/40";
                  const isJustExpanded = expandedJustification === item.id;
                  const isExpired = item.daysRemaining !== null && item.daysRemaining <= 0;

                  return (
                    <tr
                      key={item.id}
                      className={`border-b border-[#f8f9fc] dark:border-white/[0.03] transition-colors hover:bg-[#f8f9fc] dark:hover:bg-white/[0.02] ${
                        item.expiringSoon
                          ? "border-l-[3px] border-l-red-500 bg-red-50/30 dark:bg-red-500/[0.03]"
                          : ""
                      }`}
                    >
                      <td className="px-3 py-2.5 font-bold text-[#94a3b8] dark:text-white/30">
                        #{item.priorityOrder}
                      </td>
                      <td className="px-3 py-2.5 font-mono font-bold text-[#0f172a] dark:text-white/70">
                        {item.controlId?.toUpperCase()}
                      </td>
                      <td className="px-3 py-2.5 font-medium text-[#0f172a] dark:text-white/80 max-w-[200px] truncate">
                        {item.poamItemTitle}
                      </td>
                      <td className="px-3 py-2.5">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${tb.cls}`}
                        >
                          {tb.label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${lb.cls}`}
                        >
                          {lb.label}
                        </span>
                      </td>
                      <td className={`px-3 py-2.5 font-bold capitalize ${rc}`}>
                        {item.adjustedRiskLevel}
                      </td>
                      <td className="px-3 py-2.5 max-w-[220px]">
                        <div className="flex items-start gap-1">
                          <p
                            className={`text-[#64748b] dark:text-white/40 ${
                              isJustExpanded ? "" : "line-clamp-2"
                            }`}
                          >
                            {item.justification}
                          </p>
                          {item.justification.length > 80 && (
                            <button
                              onClick={() =>
                                setExpandedJustification((prev) =>
                                  prev === item.id ? null : item.id,
                                )
                              }
                              className="flex-shrink-0 text-indigo-500 dark:text-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-300 mt-0.5"
                            >
                              {isJustExpanded ? (
                                <ChevronUp className="size-3" />
                              ) : (
                                <ChevronDown className="size-3" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-[#64748b] dark:text-white/40 whitespace-nowrap">
                        {formatDate(item.effectiveUntil)}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        {item.daysRemaining === null ? (
                          <span className="text-[#94a3b8] dark:text-white/30">No Expiry</span>
                        ) : isExpired ? (
                          <span className="text-red-600 dark:text-red-400 font-black">EXPIRED</span>
                        ) : (
                          <span
                            className={`font-bold ${
                              item.expiringSoon
                                ? "text-red-600 dark:text-red-400"
                                : "text-[#0f172a] dark:text-white/70"
                            }`}
                          >
                            {item.daysRemaining}d
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
}
